import { useState } from 'react';
import toast from 'react-hot-toast';
import { contactApi } from '../lib/api';

/**
 * Public contact form modal — opens from the sign-in page so a prospect can
 * reach the team without an account. POSTs to /api/contact (no auth), which
 * routes the message to getavium@gmail.com + fires a polished auto-reply.
 *
 * Kept simple on purpose: name + email + (optional) company + message. The
 * "thanks for getting in touch" confirmation lives in the auto-reply, not in
 * this modal — we just confirm receipt and close.
 */
export default function ContactModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const close = () => {
    if (submitting) return;
    setName(''); setEmail(''); setCompany(''); setMessage(''); setSent(false);
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Name, email and message are required.');
      return;
    }
    setSubmitting(true);
    try {
      await contactApi.submit({
        name: name.trim(), email: email.trim(),
        company: company.trim() || undefined, message: message.trim(),
      });
      setSent(true);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Could not send. Try emailing getavium@gmail.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={close} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="brand-header-gradient text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">Get in touch</h2>
          <button
            onClick={close}
            aria-label="Close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-card/15 text-2xl leading-none"
          >×</button>
        </div>

        {sent ? (
          <div className="px-6 py-8 text-center">
            <div className="text-3xl mb-2">✉️</div>
            <h3 className="text-base font-bold text-ink mb-2">Message received</h3>
            <p className="text-sm text-ink-muted mb-5">
              Thanks for reaching out — we'll get back to you within one business day. A confirmation is on its way to your inbox.
            </p>
            <button
              onClick={close}
              className="px-5 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg"
            >Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-3">
            <p className="text-xs text-ink-faint mb-2">
              Questions about Avium, pricing, a demo, or something else? Drop us a line and a real person will follow up.
            </p>

            <label className="block">
              <span className="block text-xs font-semibold text-ink-muted mb-1">Your name *</span>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 text-sm border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                required maxLength={200}
              />
            </label>

            <label className="block">
              <span className="block text-xs font-semibold text-ink-muted mb-1">Email *</span>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 text-sm border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                required
              />
            </label>

            <label className="block">
              <span className="block text-xs font-semibold text-ink-muted mb-1">Company <span className="font-normal text-ink-faint">(optional)</span></span>
              <input
                value={company}
                onChange={e => setCompany(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 text-sm border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                maxLength={200}
              />
            </label>

            <label className="block">
              <span className="block text-xs font-semibold text-ink-muted mb-1">Message *</span>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                disabled={submitting}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F766E] resize-none"
                required maxLength={5000}
              />
            </label>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-ink-muted hover:bg-wash rounded-md"
              >Cancel</button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 rounded-md"
              >{submitting ? 'Sending…' : 'Send message'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
